import React, { useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetFootprintingPigletManureStorage,
  useGetPigletManureStorage,
  useKeyedFormList,
  useMutateFootprintingPigletManureStorage,
  useMutateFootprintingPigletPrimaryData,
  useTranslation,
} from "utils";

import { PIGLET_PRACTICE_SLUG, UploadableManureStorage } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const manureStoragePlaceholder: UploadableManureStorage = {
  id: undefined,
  manureStorageId: "",
  amountStoredPct: null,
};

const PigletManureStorageForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingPiglet", "piglet"]);
  const formRef = useRef<FormInstance>(null);

  const manureStorageUsagesFullQuery = useGetFootprintingPigletManureStorage(primaryDataId);
  const { data: manureStorageUsages } = manureStorageUsagesFullQuery;
  const manureStoragesFullQuery = useGetPigletManureStorage();
  const { data: manureStorages } = manureStoragesFullQuery;
  const isLoading = [manureStorageUsagesFullQuery, manureStoragesFullQuery].some(queryIsLoading);
  const keyedManureStorage = useKeyedFormList<UploadableManureStorage>(formRef, { isReady: !isLoading });

  const { mutate: mutatePigletPrimaryData } = useMutateFootprintingPigletPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      isSubmitted: false,
    },
    primaryDataId
  );

  const selectGroupName = "footprinting-piglet-manure-storage-select";

  const { mutate: mutatePigletManureStorage } = useMutateFootprintingPigletManureStorage(
    farmId,
    year,
    {
      primaryDataId: primaryDataId ?? "",
      selectedManureStorage: keyedManureStorage.payload.filter((m) => !!m.manureStorageId),
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePigletManureStorage();
      }
    },
    [isLoading, mutatePigletManureStorage, mutatePigletPrimaryData, readOnly]
  );

  // Load manure management data
  useEffect(() => {
    if (manureStorageUsages) {
      keyedManureStorage.actions.hydrate(
        manureStorageUsages.length
          ? manureStorageUsages.map((m) => ({
              id: m.id,
              manureStorageId: m.manureStorageId ?? "",
              amountStoredPct: m.amountStoredPct ?? null,
            }))
          : [manureStoragePlaceholder]
      );
      // Saved entries are checked straight away; a first-visit placeholder stays neutral
      if (manureStorageUsages.length) keyedManureStorage.actions.requestRehydrationValidation();
    }
  }, [manureStorageUsages, keyedManureStorage.actions]);

  const totalStoragePercentage = useMemo(
    () => keyedManureStorage.entries.reduce((total, storage) => total + (formatFractionInput(storage.amountStoredPct) || 0), 0),
    [keyedManureStorage.entries]
  );

  const hasLoadedManureStorage = keyedManureStorage.entries.length > 0 && manureStorageUsages !== undefined;

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef} preserve={false}>
      <Card border dataTestId="footprinting-piglet-manure-storage-form" className="padding-100">
        <Flex vertical gap={16}>
          <Flex vertical className="padding-bottom-0 padding-top-100 padding-left-100 padding-right-100">
            <Typography variant="h3">{t("pigletManureStorageForm.header")}</Typography>
            <Typography variant="paragraph-m" color="grey-600" className="margin-top-25">
              {t("pigletManureStorageForm.description")}
            </Typography>
          </Flex>

          {hasLoadedManureStorage && (
            <div className="padding-100">
              <ProgressBarCardWithContent
                currentValue={totalStoragePercentage}
                title={t("pigletManureStorageForm.progressBar.title")}
                // description={t("pigletManureStorageForm.progressBar.description")} // design decision to comment this out for now.
                errorMessage={t("pigletManureStorageForm.progressBar.errorMessage")}
                successMessage={t("pigletManureStorageForm.progressBar.successMessage")}
                size={8}
              />
            </div>
          )}

          <Flex vertical gap={16}>
            {keyedManureStorage.entries.map((manure, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Form.Item key={manure.clientKey} className="padding-left-100 padding-right-100">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" align="center" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("pigletManureStorageForm.storageNumber", { count: index + 1 })}
                      </Typography>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-manure-storage-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => keyedManureStorage.actions.remove(manure.clientKey)}
                      />
                    </Flex>
                    <Select
                      showSearch={true}
                      disabled={readOnly}
                      name={`${selectGroupName}-${manure.clientKey}`}
                      dataTestId={selectGroupWithIndex}
                      label={t("pigletManureStorageForm.question.manureStorageTypeQuestionInput")}
                      rules={[{ required: true, message: "" }]}
                      initialValue={manureStorages?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                      value={manureStorages?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                      onChange={(v) => {
                        const matchingPractice = manureStorages?.find((m) => m.name === v);
                        keyedManureStorage.actions.update(manure.clientKey, { manureStorageId: matchingPractice ? matchingPractice.id : "" });
                      }}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    >
                      {(manureStorages ?? []).map((pigletMS) => {
                        // Check if this storage ID is already used in another item
                        const isAlreadyUsed = keyedManureStorage.entries.some(
                          (m) => m.manureStorageId === pigletMS.id && m.manureStorageId !== manure.manureStorageId
                        );

                        return (
                          <Option
                            disabled={readOnly || isAlreadyUsed}
                            key={`${selectGroupWithIndex}-${pigletMS.id}`}
                            data-testid={`${selectGroupWithIndex}-${pigletMS.id}`}
                            value={pigletMS.name}
                          >
                            {t(`${PIGLET_PRACTICE_SLUG.MANURE_STORAGE}.${pigletMS.translationSlug}`, { ns: "piglet" }) || pigletMS.name}
                          </Option>
                        );
                      })}
                    </Select>

                    <InputNumber
                      key={`amountStoredPct-${manure.clientKey}-${manure.manureStorageId}`}
                      disabled={readOnly}
                      name={`amountStoredPct-${manure.clientKey}`}
                      dataTestId="footprinting-manure-storage-pct-input"
                      label={t("pigletManureStorageForm.question.manureStoragePctQuestionInput")}
                      suffix="%"
                      value={formatFractionInput(manure.amountStoredPct)}
                      initialValue={formatFractionInput(manure.amountStoredPct)}
                      onChange={(v) => keyedManureStorage.actions.update(manure.clientKey, { amountStoredPct: formatPercentageInput(v) })}
                      validateFirst
                      rules={[
                        { required: true, message: t("pigletManureStorageForm.error.validPercentage") },
                        { type: "number" },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("pigletManureStorageForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Flex>
                </Form.Item>
              );
            })}
          </Flex>

          <Button
            type="text"
            dataTestId="add-manure-storage-button"
            disabled={readOnly}
            onClick={() => {
              keyedManureStorage.actions.add(manureStoragePlaceholder);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("pigletManureStorageForm.add")}
          </Button>
        </Flex>
      </Card>
    </Form>
  );
};

PigletManureStorageForm.Key = "piglet.manureStorage";
export { PigletManureStorageForm };
